
import { Link } from 'react-router-dom'

const features = [
    {
        title: 'Zero-config deploys',
        description: 'Ship any full-stack app with one command. We infer runtime, build, and start steps automatically.',
        icon: (
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        ),
    },
    {
        title: 'Global edge builds',
        description: 'Fast builds with cached layers and region-aware Cloud Run rollouts for minimal latency.',
        icon: (
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
    {
        title: 'Cost-aware scaling',
        description: 'Scale to zero on free tier, keep warm on Pro. Right-sized memory and CPU presets.',
        icon: (
            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
    {
        title: 'Instant preview URLs',
        description: 'Every deployment gets a stable URL and a revision trail for instant rollbacks.',
        icon: (
            <svg className="h-6 w-6 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
        ),
    },
    {
        title: 'Custom Domains',
        description: 'Map your own domains with automatic SSL certificate management and rotation.',
        icon: (
            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
        ),
    },
    {
        title: 'Real-time Logs',
        description: 'Live streaming logs from your build and runtime environments directly to your CLI or dashboard.',
        icon: (
            <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
        ),
    },
]

const Features = () => {
    return (
        <div className="pb-24">
            {/* Hero Section */}
            <section className="mx-auto w-full max-w-6xl px-6 py-20 text-center">
                <div className="reveal">
                    <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
                        Powerful Primitives
                    </p>
                    <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-slate-900 md:text-6xl">
                        Everything you need to <br className="hidden md:block" />
                        <span className="text-slate-600">run in production.</span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600">
                        Flynt abstracts the complexity of cloud infrastructure while giving you the power to scale.
                        Focus on your code, we handle the rest.
                    </p>
                </div>
            </section>

            {/* Main Grid */}
            <section className="mx-auto w-full max-w-6xl px-6">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {features.map((feature, index) => (
                        <div
                            key={feature.title}
                            className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 transition-all hover:-translate-y-1 hover:shadow-xl reveal"
                            style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 transition-colors group-hover:bg-slate-100">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">{feature.title}</h3>
                            <p className="mt-3 text-slate-600 leading-relaxed">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Deep Dive Section 1 */}
            <section className="mx-auto mt-32 w-full max-w-6xl px-6">
                <div className="flex flex-col gap-16 lg:flex-row lg:items-center">
                    <div className="flex-1 reveal">
                        <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-600"></span>
                            Runtime Agnostic
                        </div>
                        <h2 className="mt-6 text-3xl font-bold text-slate-900 md:text-4xl">
                            Bring your own framework.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">
                            We automatically detect your project type—whether it's Node, Python, Go, or a static site to setup the optimal build environment.
                        </p>
                        <ul className="mt-8 space-y-4">
                            {['Auto-detection for 15+ frameworks', 'Custom Dockerfile support', 'Multi-stage build caching', 'Zero-downtime rollouts'].map((item) => (
                                <li key={item} className="flex items-center gap-3 text-slate-700">
                                    <svg className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10">
                            <Link to="/how-it-works" className="text-blue-600 font-semibold hover:text-blue-700 hover:underline">
                                See how it works &rarr;
                            </Link>
                        </div>
                    </div>
                    <div className="flex-1 reveal" style={{ animationDelay: '200ms' }}>
                        <div className="relative rounded-2xl border border-slate-200 bg-slate-900 shadow-2xl">
                            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
                                <div className="h-3 w-3 rounded-full bg-red-500" />
                                <div className="h-3 w-3 rounded-full bg-amber-500" />
                                <div className="h-3 w-3 rounded-full bg-emerald-500" />
                            </div>
                            <div className="p-6 font-mono text-sm text-slate-300">
                                <div className="flex gap-2">
                                    <span className="text-emerald-400">➜</span>
                                    <span>~</span>
                                    <span className="text-white">inflynx detect</span>
                                </div>
                                <div className="mt-2 text-slate-400">Scanning project structure...</div>
                                <div className="mt-2 text-white">✓ Detected Next.js project</div>
                                <div className="mt-1 text-white">✓ Found package.json</div>
                                <div className="mt-1 text-white">✓ Node.js v20.x selected</div>
                                <div className="mt-4 flex gap-2">
                                    <span className="text-emerald-400">➜</span>
                                    <span>~</span>
                                    <span className="text-white animate-pulse">_</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Deep Dive Section 2 */}
            <section className="mx-auto mt-32 w-full max-w-6xl px-6">
                <div className="flex flex-col gap-16 lg:flex-row-reverse lg:items-center">
                    <div className="flex-1 reveal">
                        <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-purple-600"></span>
                            Global Edge
                        </div>
                        <h2 className="mt-6 text-3xl font-bold text-slate-900 md:text-4xl">
                            Low latency, worldwide.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">
                            Deploy once, and we route traffic to the nearest healthy instance. Your users get the fastest possible response time, no matter where they are.
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-6">
                            <div>
                                <div className="text-3xl font-bold text-slate-900">35+</div>
                                <div className="text-sm text-slate-500">Regions</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">99.99%</div>
                                <div className="text-sm text-slate-500">Uptime SLA</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 reveal relative flex items-center justify-center" style={{ animationDelay: '200ms' }}>
                        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 blur-3xl rounded-full" />
                        <img src="/logo.jpeg" className="relative w-48 h-48 object-contain drop-shadow-2xl" alt="Global Network" />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mt-32 border-t border-slate-200 bg-slate-50">
                <div className="mx-auto max-w-4xl px-6 py-24 text-center">
                    <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
                        Ready to ship?
                    </h2>
                    <p className="mt-4 text-lg text-slate-600">
                        Join thousands of developers building the future of the web.
                    </p>
                    <div className="mt-8 flex justify-center gap-4">
                        <Link to="/signup" className="rounded-full bg-slate-900 px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800 hover:-translate-y-0.5">
                            Start Building Free
                        </Link>
                        <Link to="/pricing" className="rounded-full border border-slate-300 bg-white px-8 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:-translate-y-0.5">
                            View Pricing
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Features
